/**
 * Auth Initialization Hook
 * Hydrates Redux store with user data from localStorage on app startup
 *
 * This hook ensures that user data persisted in localStorage is properly
 * synchronized with the Redux store when the app loads or refreshes.
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import userActions from '@/redux/actions/user';

/**
 * Hook to initialize auth state from localStorage
 * Should be called once at the app root level
 */
export const useAuthInit = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);
  const initialized = useRef(false);

  useEffect(() => {
    // Only run once on mount
    if (initialized.current) return;
    initialized.current = true;

    // If Redux already has user data, no need to hydrate
    if (user) return;

    // Try to get user from localStorage
    try {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('access_token');

      // Validate both user data and token exist
      if (!userStr || !token) {
        return;
      }

      // Check for invalid values
      if (userStr === 'undefined' || userStr === 'null' || userStr.trim() === '') {
        localStorage.removeItem('user');
        return;
      }

      if (token === 'undefined' || token === 'null') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        return;
      }

      // Parse and validate user data
      const userData = JSON.parse(userStr);

      if (!userData || typeof userData !== 'object' || !userData.id) {
        localStorage.removeItem('user');
        return;
      }

      // Dispatch user data to Redux store
      dispatch(userActions.login(userData) as any);

    } catch (error) {
      console.error('Error hydrating auth state from localStorage:', error);
      // Clean up corrupted data
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
    }
  }, [dispatch, user]);
};

export default useAuthInit;
